import { ChangeDetectionStrategy, Component, HostBinding, Input, OnInit } from '@angular/core';

import { RewardTargetType } from '@bpos/v1/sports-promo/tokens';
import { CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { OddsFormatterService, OddsOperations } from '@frontend/sports/odds/feature';
import { UserService } from '@frontend/sports/user/feature';
import { IntlService } from '@frontend/vanilla/core';

import { PayoutType } from '../../../../../../../lib/tokens-base/token-base.models';
import { TokensSummaryService } from '../../../../../../../lib/tokens-base/tokens-summary.service';
import { CurrencyPipe } from '../../../../../../base/pipes/currency.pipe';
import { RiskFreeToken } from '../../../../reward-tokens.model';
import { RewardTokensBannerService, TokenCategories } from '../../../../services/reward-tokens-banner.service';
import { getPayoutExpiryInDays } from '../../../../services/reward-tokens.utils';
import { tokenBoostAnimations } from '../../token-boost-animations';
import { RewardTokenCardBaseComponent } from '../reward-token-card-base.component';
import { RewardTokenLimitation } from '../reward-token-limitations.component';

@Component({
    selector: 'bs-risk-free-card',
    templateUrl: './risk-free-card.component.html',
    styleUrls: ['../../cards/styles/reward-token-card-shared.scss', './risk-free-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [tokenBoostAnimations],
    standalone: false,
})
export class RiskFreeCardComponent extends RewardTokenCardBaseComponent<RiskFreeToken> implements OnInit {
    @HostBinding('class') className = 'risk-free-card';

    @HostBinding('class.reward-drawer__details') get formCoupon(): boolean {
        return this.isRewardDrawerEnabled;
    }

    expireTokenReward: RewardTokenLimitation[];
    betType: RewardTokenLimitation[];
    @Input() override isRewardDrawerEnabled: boolean;
    promoHubUrlTokenType: string;
    tokenPayoutTitle: string;
    tokenLimitations: RewardTokenLimitation[];
    tokenCategories: TokenCategories;

    constructor(
        public override sitecore: Sitecore,
        private interpolationPipe: StringInterpolationPipe,
        protected userService: UserService,
        protected override crmConfig: CrmConfig,
        protected override intlService: IntlService,
        private oddsFormatter: OddsFormatterService,
        private currencyPipe: CurrencyPipe,
        protected override tokensPayoutInfo: TokensSummaryService,
        private rewardTokensBannerService: RewardTokensBannerService,
    ) {
        super(sitecore, crmConfig, userService, intlService, oddsFormatter, tokensPayoutInfo);
    }

    ngOnInit(): void {
        this.expireTokenReward = this.getTokenUserExpire();
        this.betType = this.getTokenBetType();
        this.tokenPayoutTitle = this.isRewardDrawerEnabled
            ? `${this.sitecore.crmTokens.cards.TokenPayout}:`
            : this.sitecore.crmTokens.riskFree.card.Payout;
        this.tokenLimitations = this.getTokenLimitations();
        this.promoHubUrlTokenType = this.sitecore.crmTokens.ctaLinks.promoCtaLinks.RiskFreeBet;
        this.tokenCategories = this.rewardTokensBannerService.getTokenCategories(this.token);
    }

    override getTokenLimitations(): RewardTokenLimitation[] {
        const result: RewardTokenLimitation[] = super.getTokenLimitations();

        if (this.token.minimumStake) {
            result.push({
                name: this.sitecore.crmTokens.riskFree.card.MinStake,
                value: this.currencyPipe.transform(this.token.minimumStake, true, 2, true, undefined, undefined, false),
            });
        }

        return result;
    }

    get tokenTitle(): string {
        return this.token.customTitle || this.getDefaultTitle();
    }

    private getDefaultTitle(): string {
        if (this.token.rewardTargetType === RewardTargetType.WelcomeOffer) {
            return this.sitecore.crmTokens.messages.WelcomeOfferTitle;
        }

        return this.sitecore.crmTokens.riskFree.card.DefaultTitle;
    }

    get maxReturnDetails(): string {
        return this.interpolationPipe.transform(this.sitecore.crmTokens.riskFree.card.MaxReturnTemplate, {
            percentageMaxReturn: this.token.riskFreePercentage.toString(),
            maxReturnAmount: this.currencyPipe.transform(this.token.maximumPayout, true, 2, true, undefined, undefined, false),
        });
    }

    get payoutDetails(): string {
        switch (this.token.payoutType) {
            case PayoutType.FreeBet:
                return this.isRewardDrawerEnabled
                    ? `${this.maxReturnDetails} ${this.sitecore.crmTokens.riskFree.card.FreebetPayoutTemplate}`
                    : this.interpolationPipe.transform(this.sitecore.crmTokens.riskFree.card.FreebetPayoutTemplate, {
                          payoutExpiry: getPayoutExpiryInDays(this.token),
                      });
            case PayoutType.Bonus:
                const oddsFormat = this.userOddsFormatSettingsService.oddsFormat;

                return this.isRewardDrawerEnabled
                    ? `${this.maxReturnDetails} ${this.sitecore.crmTokens.riskFree.card.BonusPayoutTemplate}`
                    : this.interpolationPipe.transform(this.sitecore.crmTokens.riskFree.card.BonusPayoutTemplate, {
                          wageringMultiplier: this.token.wageringMultiplier ? this.token.wageringMultiplier.toString() : '',
                          minOdds: this.token.minOdds
                              ? this.oddsFormatter.format(OddsOperations.createOdds({ odds: this.token.minOdds }), { displayFormat: oddsFormat })
                              : '',
                          payoutExpiry: getPayoutExpiryInDays(this.token),
                      });
            case PayoutType.Cash:
                return this.isRewardDrawerEnabled
                    ? `${this.maxReturnDetails} ${this.sitecore.crmTokens.riskFree.card.CashPayoutTemplate}`
                    : this.sitecore.crmTokens.riskFree.card.CashPayoutTemplate;
        }

        return '';
    }
}
